import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  Calendar,
  Command,
  Home,
  LogOut,
  Search,
  Settings,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuth from "@integration/user/hooks/useAuth.ts";

interface CommandOption {
  category: string;
  items: {
    label: string;
    icon: React.ReactNode;
    action: () => void;
  }[];
}

const CommandMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { logout } = useAuth();

  const options: CommandOption[] = [
    {
      category: "Navegação",
      items: [
        {
          label: "Início",
          icon: <Home size={18} />,
          action: () => navigate("/"),
        },
        {
          label: "Campeonatos",
          icon: <Trophy size={18} />,
          action: () => navigate("/championships"),
        },
        {
          label: "Eventos",
          icon: <Calendar size={18} />,
          action: () => navigate("/events"),
        },
        {
          label: "Notificações",
          icon: <Bell size={18} />,
          action: () => navigate("/notifications"),
        },
      ],
    },
    {
      category: "Perfil",
      items: [
        {
          label: "Meu Perfil",
          icon: <Users size={18} />,
          action: () => navigate("/profile"),
        },
        {
          label: "Configurações",
          icon: <Settings size={18} />,
          action: () => navigate("/settings"),
        },
        { label: "Sair", icon: <LogOut size={18} />, action: () => logout() },
      ],
    },
  ];

  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (search.trim() === "") {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options
          .map((group) => ({
            ...group,
            items: group.items.filter((item) =>
              item.label.toLowerCase().includes(search.toLowerCase())
            ),
          }))
          .filter((group) => group.items.length > 0)
      );
    }
  }, [search]);

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className="group ml-1  w-fit md:flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white bg-gradient-to-b from-gray-800/60 to-gray-900/60 hover:from-gray-700/60 hover:to-gray-800/60 rounded-lg transition-all border border-gray-700/50 hover:border-red-500/30 shadow-md backdrop-blur-sm"
        whileHover={{ scale: 1.03, y: -1 }}
        whileTap={{ scale: 0.97 }}
      >
        <Search
          size={16}
          className="text-red-400 transition-transform group-hover:scale-105"
        />
        <span className="hidden sm:block">Buscar...</span>
        <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs font-medium text-gray-400 bg-gray-800/80 rounded border border-gray-700/50 transition-transform group-hover:scale-105">
          <Command size={12} className="mr-1 text-red-400" />K
        </kbd>
      </motion.button>

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <AnimatePresence>
          {isOpen && (
            <Dialog.Portal>
              <Dialog.Overlay asChild>
                <motion.div
                  className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9000]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              </Dialog.Overlay>

              <Dialog.Content asChild>
                <motion.div
                  className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-b from-gray-800/80 to-gray-900/80 rounded-xl shadow-2xl p-6 max-w-2xl w-full z-[9999] border border-red-500/20 backdrop-blur-sm overflow-hidden"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>
                  <div className="flex relative justify-between items-center mb-6 z-10">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-2.5 rounded-xl shadow-md border border-red-500/10">
                        <Command size={20} className="text-red-400" />
                      </div>
                      <h2 className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                        Comando Rápido
                      </h2>
                    </div>
                    <Dialog.Close asChild>
                      <motion.button
                        className="text-gray-400 hover:text-red-400 transition-all hover:bg-gray-800/80 p-1.5 rounded-full border border-gray-700/50 hover:border-red-500/30"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X size={18} />
                      </motion.button>
                    </Dialog.Close>
                  </div>
                  <div className="h-0.5 w-32 bg-gradient-to-r from-red-500/30 to-transparent mb-6 relative z-10"></div>

                  <div className="relative mb-6 bg-gradient-to-b from-gray-800/40 to-gray-900/40 rounded-xl overflow-hidden border border-gray-700/50 hover:border-red-500/30 transition-all duration-300 shadow-inner z-10">
                    <div className="flex items-center">
                      <div className="ml-4 bg-gradient-to-br from-red-500/20 to-red-700/20 p-1.5 rounded-lg shadow-sm border border-red-500/10">
                        <Search className="text-red-400" size={16} />
                      </div>
                      <input
                        type="text"
                        placeholder="Digite para buscar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                        className="w-full px-4 py-3.5 outline-none bg-transparent text-white placeholder-gray-400 focus:placeholder-gray-500 transition-all duration-300"
                      />
                    </div>
                  </div>

                  <div className="max-h-[60vh] overflow-y-auto scrollbar-custom">
                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((group, index) => (
                        <motion.div
                          key={index}
                          className="mb-6 p-1 last:mb-2 relative"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-1.5 rounded-lg shadow-sm border border-red-500/10">
                              {group.category === "Navegação" ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-red-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-red-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              )}
                            </div>
                            <h3 className="text-sm font-semibold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                              {group.category}
                            </h3>
                          </div>
                          <div className="h-0.5 w-16 bg-gradient-to-r from-red-500/30 to-transparent mb-3"></div>
                          <div className="space-y-1.5">
                            {group.items.map((item, i) => (
                              <motion.button
                                key={i}
                                onClick={() => {
                                  item.action();
                                  setIsOpen(false);
                                }}
                                className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-gradient-to-b hover:from-gray-800/60 hover:to-gray-900/60 transition-all flex items-center gap-3 text-gray-300 hover:text-white group border border-transparent hover:border-gray-700/50 hover:shadow-md"
                                whileHover={{ x: 4, scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 10,
                                }}
                              >
                                <span className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-1.5 rounded-lg transition-all group-hover:from-red-500/20 group-hover:to-red-700/20 border border-gray-700/50 group-hover:border-red-500/30">
                                  {item.icon}
                                </span>
                                <span>{item.label}</span>
                              </motion.button>
                            ))}
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center py-8 bg-gradient-to-b from-gray-800/30 to-gray-900/30 rounded-xl border border-gray-700/50 p-8 shadow-lg backdrop-blur-sm relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>

                        <div className="relative">
                          <div className="bg-gradient-to-br from-gray-700/50 to-gray-800/50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-gray-600/30 shadow-inner">
                            <Search size={24} className="text-gray-400" />
                          </div>

                          <h3 className="text-lg font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">
                            Nenhum resultado encontrado
                          </h3>

                          <div className="h-0.5 w-24 bg-gradient-to-r from-gray-500/30 to-transparent mx-auto mb-3"></div>

                          <p className="text-sm text-gray-400">
                            Tente buscar por outro termo
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </Dialog.Root>
    </>
  );
};

export default CommandMenu;
